import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ShieldCheck, Clock, Award, X, ArrowRight } from 'lucide-react';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6
  }
};
type Provider = {
  id: string;
  name: string;
  specialty: string;
  years: number;
  quote: string;
  benefit: string;
  image: string;
};
const categories = [{
  name: 'Legal & Residency',
  description: 'Navigate Mexican law and immigration with ease.',
  providers: [{
    id: 'l1',
    name: 'Lex Advisors Cabo',
    specialty: 'Real Estate Closings & Entity Setup',
    years: 15,
    quote: 'They made setting up our Mexican corporation entirely painless. Flawless execution.',
    benefit: 'Collective Price: $6,000 (vs standard $10,000) & 7-day turnkey entity setup.',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80'
  }, {
    id: 'l2',
    name: 'Baja Immigration Services',
    specialty: 'Permanent Residency & Visas',
    years: 12,
    quote: 'Got our permanent residency sorted while we were still in the US. Incredible service.',
    benefit: 'Fast-track: 30 days (vs standard 6-12 months) for $5,000.',
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&q=80'
  }]
}, {
  name: 'Design & Build',
  description: 'Transform your property with trusted visionaries.',
  providers: [{
    id: 'd1',
    name: 'Cabo Constructora',
    specialty: 'General Contracting & New Builds',
    years: 20,
    quote: 'On time, on budget, and the craftsmanship is world-class. No surprises.',
    benefit: 'Priority scheduling: Start within 60 days (vs standard 12-18 month waitlist).',
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&q=80'
  }, {
    id: 'd2',
    name: 'Studio Baja',
    specialty: 'Luxury Interior Design',
    years: 8,
    quote: 'They perfectly captured the indoor-outdoor Cabo aesthetic we dreamed of.',
    benefit: 'Complimentary initial 3D rendering and 15% off custom furniture sourcing.',
    image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&q=80'
  }]
}];
export function Collective() {
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleConnect = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this sends the lead to the backend for referral tracking
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setSelectedProvider(null);
      setLeadForm({
        name: '',
        email: '',
        phone: ''
      });
    }, 3000);
  };
  return <div className="w-full bg-sand min-h-screen pt-24" data-id="element-396" data-mp="6q5kf2subkeg">
      {/* Header */}
      <section className="py-20 px-4 bg-navy text-sand text-center" data-id="element-397" data-mp="aqph659i4m02">
        <div className="max-w-4xl mx-auto" data-id="element-398" data-mp="3t5hxdd6meirz">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-serif text-white mb-6" data-id="element-399" data-mp="3t4f9zf8iaygt">
            The Collective
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-lg md:text-xl font-light text-sand/80 leading-relaxed mb-10" data-id="element-400" data-mp="3o4kgr519dwsl">
            A curated directory of vetted, elite professionals. We've done the
            background checks, verified the track records, and negotiated
            exclusive benefits so you can build, buy, and live in Cabo with
            absolute confidence.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="flex flex-wrap justify-center gap-8 text-sm uppercase tracking-wider font-semibold text-gold" data-id="element-401" data-mp="2tcbhvbr7btrb">
            <div className="flex items-center" data-id="element-402" data-mp="2ahczs23i76nv">
              <ShieldCheck className="mr-2" size={18} data-id="element-403" data-mp="cps8i45sdv41" /> Vetted
            </div>
            <div className="flex items-center" data-id="element-404" data-mp="8o6o7xqxaejt">
              <Award className="mr-2" size={18} data-id="element-405" data-mp="8of1vruvdnw2" /> Proven
            </div>
            <div className="flex items-center" data-id="element-406" data-mp="1qmbo1t1qiz4u">
              <Star className="mr-2" size={18} data-id="element-407" data-mp="1qjfqi5tsucgx" /> Exclusive Pricing
            </div>
          </motion.div>
        </div>
      </section>

      {/* Directory */}
      <section className="py-20 px-4 max-w-7xl mx-auto" data-id="element-408" data-mp="20cegns4y2b2n">
        {categories.map((category, idx) => <div key={idx} className="mb-24" data-id="element-409" data-mp="24bn9mus5u4x5" data-mpi={idx}>
            <motion.div {...fadeIn} className="mb-12 border-b border-navy/10 pb-6" data-id="element-410" data-mp="24ernykzcah1j">
              <h2 className="text-3xl font-serif text-navy mb-2" data-id="element-411" data-mp="1cl917e2x1ksv">
                {category.name}
              </h2>
              <p className="text-navy/60 font-light text-lg" data-id="element-412" data-mp="1gmb0fuwu1wuu">
                {category.description}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-413" data-mp="1lkcn0ptgweb2">
              {category.providers.map((provider, pIdx) => <motion.div key={provider.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: pIdx * 0.1
          }} className="bg-white flex flex-col h-full shadow-sm hover:shadow-md transition-shadow" data-id="element-414" data-mp="2x12vkfld5i7u" data-mpi={pIdx}>
                  <div className="h-48 overflow-hidden relative" data-id="element-415" data-mp="99s9muqdzmev">
                    <img src={provider.image} alt={provider.name} className="w-full h-full object-cover" data-id="element-416" data-mp="9blgob4ww8ne" />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 text-xs font-bold text-navy uppercase tracking-wider" data-id="element-417" data-mp="j6dg7ki215kt">
                      {provider.years} Years Exp.
                    </div>
                  </div>
                  <div className="p-8 flex-grow flex flex-col" data-id="element-418" data-mp="22648sc55daek">
                    <h3 className="text-2xl font-serif text-navy mb-1" data-id="element-419" data-mp="224jemnmqvth8">
                      {provider.name}
                    </h3>
                    <p className="text-gold text-sm font-semibold uppercase tracking-wider mb-6" data-id="element-420" data-mp="1t6hhij64tkz8">
                      {provider.specialty}
                    </p>

                    <blockquote className="border-l-2 border-gold pl-4 text-navy/70 font-light italic mb-6 flex-grow" data-id="element-421" data-mp="1x5gg7m19sli8">
                      "{provider.quote}"
                    </blockquote>

                    <div className="bg-sand p-4 mb-6" data-id="element-422" data-mp="2pu48w2ocmd5t">
                      <div className="flex items-start" data-id="element-423" data-mp="2bve19t3wkv37">
                        <Star className="text-gold mr-3 shrink-0 mt-0.5" size={18} data-id="element-424" data-mp="2gvijbnkglxvj" />
                        <p className="text-sm text-navy font-medium" data-id="element-425" data-mp="10fpeb1qnfw2e">
                          <span className="block text-xs text-navy/60 uppercase tracking-wider mb-1" data-id="element-426" data-mp="15gn522pq5yfi">
                            Collective Benefit
                          </span>
                          {provider.benefit}
                        </p>
                      </div>
                    </div>

                    <button onClick={() => setSelectedProvider(provider)} className="w-full py-3 border border-navy text-navy uppercase tracking-wider text-sm font-semibold hover:bg-navy hover:text-white transition-colors flex items-center justify-center group" data-id="element-427" data-mp="1n6tn7kqo8ghh">
                      Connect with {provider.name.split(' ')[0]}
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} data-id="element-428" data-mp="263usz7s1ukmh" />
                    </button>
                  </div>
                </motion.div>)}
            </div>
          </div>)}
      </section>

      {/* Lead Capture Modal */}
      <AnimatePresence data-id="element-429" data-mp="315db4m6x428s">
        {selectedProvider && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/80 backdrop-blur-sm" data-id="element-430" data-mp="316wq89gu878z">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white w-full max-w-lg relative overflow-hidden shadow-2xl" data-id="element-431" data-mp="364otfzmxnfsh">
              <button onClick={() => setSelectedProvider(null)} className="absolute top-4 right-4 text-navy/50 hover:text-navy z-10" data-id="element-432" data-mp="3p0y5yii6ryad">
                <X size={24} data-id="element-433" data-mp="2myc8aa8xllm" />
              </button>

              <div className="p-8 md:p-10" data-id="element-434" data-mp="7nlykinj5e9i">
                {submitted ? <div className="text-center py-10" data-id="element-435" data-mp="bo4sci8zguec">
                    <ShieldCheck className="mx-auto text-gold mb-4" size={48} data-id="element-436" data-mp="boxuf5wzyvq5" />
                    <h3 className="text-2xl font-serif text-navy mb-2" data-id="element-437" data-mp="1dnod54viaqpz">
                      Introduction Sent
                    </h3>
                    <p className="text-navy/70 font-light" data-id="element-438" data-mp="1ijn675rs4wwq">
                      We've notified {selectedProvider.name}. They will reach
                      out to you shortly with your exclusive Collective
                      benefits.
                    </p>
                  </div> : <>
                    <h3 className="text-2xl font-serif text-navy mb-2" data-id="element-439" data-mp="21boainnbpfdj">
                      Connect with {selectedProvider.name}
                    </h3>
                    <p className="text-navy/60 font-light mb-8 text-sm" data-id="element-440" data-mp="26c3pfjn96uxm">
                      Provide your details below to claim your exclusive
                      Collective benefits and initiate an introduction.
                    </p>

                    <form onSubmit={handleConnect} className="space-y-4" data-id="element-441" data-mp="gvuelhp899wg">
                      <div data-id="element-442" data-mp="gy74qi552qkl">
                        <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1" data-id="element-443" data-mp="9dcz8uuo70vk">
                          Full Name
                        </label>
                        <input type="text" required value={leadForm.name} onChange={e => setLeadForm({
                    ...leadForm,
                    name: e.target.value
                  })} className="w-full px-4 py-3 bg-sand border border-navy/10 focus:outline-none focus:border-gold font-light" data-id="element-444" data-mp="3vs68asfmxbe2" />
                      </div>
                      <div data-id="element-445" data-mp="dgj90cgzra6s">
                        <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1" data-id="element-446" data-mp="333ieg8gtldow">
                          Email Address
                        </label>
                        <input type="email" required value={leadForm.email} onChange={e => setLeadForm({
                    ...leadForm,
                    email: e.target.value
                  })} className="w-full px-4 py-3 bg-sand border border-navy/10 focus:outline-none focus:border-gold font-light" data-id="element-447" data-mp="1q58iriesnf6v" />
                      </div>
                      <div data-id="element-448" data-mp="1ba2dixnivkel">
                        <label className="block text-xs font-semibold text-navy uppercase tracking-wider mb-1" data-id="element-449" data-mp="xgt7n6ys9h0t">
                          Phone Number (Optional)
                        </label>
                        <input type="tel" value={leadForm.phone} onChange={e => setLeadForm({
                    ...leadForm,
                    phone: e.target.value
                  })} className="w-full px-4 py-3 bg-sand border border-navy/10 focus:outline-none focus:border-gold font-light" data-id="element-450" data-mp="nidhb67kybsd" />
                      </div>
                      <button type="submit" className="w-full mt-4 bg-gold text-white py-4 uppercase tracking-wider text-sm font-semibold hover:bg-gold-dark transition-colors" data-id="element-451" data-mp="dtzqhbrw032m">
                        Request Introduction
                      </button>
                      <p className="text-xs text-center text-navy/40 mt-4" data-id="element-452" data-mp="11jkdc5r5vkh6">
                        By requesting an introduction, you agree to our referral
                        terms.
                      </p>
                    </form>
                  </>}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}